import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import AboutHeroCustom from '../components/About/Hero';


const BLUE = '#0B4DA2';
const GOLD = '#D7AB50';
const BROWN = '#5C3B25';

const coreValues = [
  { letter: 'C', label: 'COMMITMENT', img: '/images/About_us/CARE.png' },
  { letter: 'A', label: 'ACCURACY', img: '/images/About_us/CARE.1.png' },
  { letter: 'R', label: 'RESPONSIBILITY', img: '/images/About_us/CARE.2.png' },
  { letter: 'E', label: 'EXCELLENT', img: '/images/About_us/CARE.3.png' },
];

const About: React.FC = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Get the hash from the URL
    const hash = window.location.hash;
    
    if (hash) {
      // Remove the # from the hash
      const elementId = hash.substring(1);
      // Find the element
      const element = document.getElementById(elementId);
      
      if (element) {
        // Scroll the element into view with smooth behavior
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []); // Empty dependency array means this runs once when component mounts

  return (
    <>
      <AboutHeroCustom />

      {/* Who Are We */}
      <section id="who-are-we" className="pt-4 pb-1 bg-white margin-top-75">
        <Container>
          <Row className="mb-5 who-are-we-row">
            <Col md={5} className="mb-4 mb-md-0 px-0">
              <img src="/images/About_us/1.jpg" alt="Who Are We" className="who-are-we-img image-hover-animation rounded" />
            </Col>
            <Col md={7} className="px-0">
              <div className='who-are-we-text'>
                <h4 className="fw-bold text-uppercase mb-2 header-2 color-brown" style={{ borderLeft: `2px solid ${BROWN}`, paddingLeft: 20 }}>{t('about.whoAreWe.title')}</h4>
                <p className='body-text text-justify color-dark'>
                  <span className='body-text'>{t('about.whoAreWe.company')}</span><span className='body-text-header color-brown'>{t('about.whoAreWe.header')}</span>
                  {t('about.whoAreWe.description')}
                </p>
              </div>
            </Col>
          </Row>

          {/* Company History */}
          <Row id="company-history" className="mb-5 company-history-row">
            <Col md={12} className="px-0">
              <img src="/images/About_us/2.jpg" alt="Company History" className="company-history-img image-hover-animation mb-3 rounded" />
              <div className='company-history-text'>
                <h4 className="fw-bold text-uppercase mb-2 header-2 color-brown" style={{ borderLeft: `2px solid ${BROWN}`, paddingLeft: 20 }}>{t('about.history.title')}</h4>
                <p className='body-text text-justify color-dark'>
                  <span className='body-text'>{t('about.history.company')}</span><span className='body-text-header color-brown'>{t('about.history.header')}</span>
                  {t('about.history.description')}
                </p>
              </div>
            </Col>
          </Row>

          {/* Management Team Section */}
          <Row className="mb-5 justify-content-center management-team-row">
            <Col md={12} className="text-center">
              <h2 className="fw-bold text-uppercase header-2" style={{ color: '#5A3A0A', letterSpacing: 1 }}>{t('about.managementTeam.title')}</h2>
            </Col>
            {/* Top row: 1 */}
            <Col md={12} className="d-flex flex-column align-items-center p-0">
              <div className='management-image-container'>
                <img src="/images/About_us/3.1.jpg" alt={t('about.managementTeam.president.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.president.name')}</div>
              <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.president.position')}</div>
            </Col>
            {/* Second row: 3 */}
            <Col md={12} className="d-flex flex-row justify-content-center align-items-start p-0 flex-wrap management-gap">
              <div className="d-flex flex-column align-items-center">
                <div className='management-image-container'>
                  <img src="/images/About_us/3.2.jpg" alt={t('about.managementTeam.md.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.md.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.md.position')}</div>
              </div>
              <div className="d-flex flex-column align-items-center">
                <div className='management-image-container'>
                  <img src="/images/About_us/3.3.jpg" alt={t('about.managementTeam.ca.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.ca.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.ca.position')}</div>
              </div>
              <div className="d-flex flex-column align-items-center">
                <div className='management-image-container'>
                  <img src="/images/About_us/3.4.jpg" alt={t('about.managementTeam.coo.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.coo.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.coo.position')}</div>
              </div>
            </Col>
            {/* Third row: 3 */}
            <Col md={12} className="d-flex flex-row justify-content-center align-items-start p-0 flex-wrap management-gap-3">
              <div className="d-flex flex-column align-items-center">
                <div className='management-image-container'>
                  <img src="/images/About_us/3.5-1.jpg" alt={t('about.managementTeam.exportManager.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.exportManager.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.exportManager.position')}</div>
              </div>
              <div className="d-flex flex-column align-items-center">
                <div className='management-middle-image-container'>
                  <img src="/images/About_us/3.6.jpg" alt={t('about.managementTeam.importManager.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.importManager.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.importManager.position')}</div>
              </div>
              <div className="d-flex flex-column align-items-center">
                <div className='management-image-container'>
                  <img src="/images/About_us/3.7-2.jpg" alt={t('about.managementTeam.hrManager.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.hrManager.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.hrManager.position')}</div>
              </div>
            </Col>
            {/* Bottom row: 2 */}
            <Col md={12} className="d-flex flex-row justify-content-center align-items-start p-0 flex-wrap management-gap">
              <div className="d-flex flex-column align-items-center">
                <div className='management-image-container'>
                  <img src="/images/About_us/3.8-1.jpg" alt={t('about.managementTeam.financeManager.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.financeManager.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.financeManager.position')}</div>
              </div>
              <div className="d-flex flex-column align-items-center">
                <div className='management-image-container'>
                  <img src="/images/About_us/3.9-1.jpg" alt={t('about.managementTeam.agriManager.name')} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div className='menu-item color-brown fw-bold management-name'>{t('about.managementTeam.agriManager.name')}</div>
                <div className='menu-item color-secondary fw-bold management-position'>{t('about.managementTeam.agriManager.position')}</div>
              </div>
            </Col>
          </Row>

          {/* President's Message Section */}
          <Row className="align-items-start president-message mb-5" style={{ borderBottom: '0.5px solid #D7AB50', paddingBottom: 24 }}>
            <Col lg={6} className="ps-0 column-left">
              <div className="about-message-card position-relative rounded">
                <div className="about-news-category">{t('news.category')}</div>
                <img 
                  src="/images/About_us/5.jpg" 
                  alt={t('about.presidentMessage.title')} 
                  className="about-message-image w-100 h-100 image-hover-animation"
                />
                <div className="about-message-overlay">
                  <h3>{t('about.presidentMessage.title')}</h3>
                  <p className='president-name'>{t('about.presidentMessage.name')}</p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="pe-0 column-right">
              <div className="message-content">
                <h2 className="message-title with-left-bar header-2 color-brown">{t('about.presidentMessage.title')}</h2>
                <div className="message-text">
                  <p className='text-justify body-text'>{t('about.presidentMessage.paragraph1')}</p>
                  <p className='text-justify body-text'>{t('about.presidentMessage.paragraph2')}</p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Managing Director's Message Section */}
          <Row className="align-items-start mb-5" style={{ borderBottom: '0.5px solid #D7AB50', paddingBottom: 24 }}>
            <Col lg={6} className="ps-0 column-left">
              <div className="message-content">
                <h2 className="message-title with-left-bar header-2 color-brown">{t('about.mdMessage.title')}</h2>
                <div className="message-text">
                  <p className='text-justify body-text'>{t('about.mdMessage.paragraph1-1')}<span className='menu-item color-brown'>{t('about.history.header')}</span>{t('about.mdMessage.paragraph1-2')}</p>
                  <p className='text-justify body-text'>{t('about.mdMessage.paragraph2')}</p>
                  <p className='text-justify body-text'><span className='body-text'>{t('about.history.company')}</span><span className='menu-item color-brown'>{t('about.history.header')}</span>{t('about.mdMessage.paragraph3')}</p>
                </div>
              </div>
            </Col>
            <Col lg={6} className="pe-0 column-right">
              <div className="about-message-card position-relative rounded">
                <div className="about-news-category">{t('news.category')}</div>
                <img 
                  src="/images/About_us/6.jpg" 
                  alt={t('about.mdMessage.title')} 
                  className="about-message-image w-100 h-100 image-hover-animation"
                />
                <div className="about-message-overlay">
                  <h3>{t('about.mdMessage.title')}</h3>
                  <p>{t('about.mdMessage.name')}</p>
                </div>
              </div>
            </Col>
          </Row>

          {/* Mission & Vision */}
          <Row className="mb-5">
            <Col md={12} className="mb-4 px-0 mission-column">
              <div id="mission" className="d-flex flex-md-row flex-column align-items-stretch mission-vision-section p-3" style={{ background: GOLD, overflow: 'hidden' }}>
                <img src="/images/About_us/7.jpg" alt="Our Mission" className="image-hover-animation" />
                <div className="px-4 py-1 d-flex flex-column justify-content-start" style={{ flex: 1 }}>
                  <div className="d-flex align-items-center mb-2">
                    <span className="mission-header-bar me-3"></span>
                    <h5 className="fw-bold text-uppercase text-white mb-2 header-2">{t('about.mission.title')}</h5>
                  </div>
                  <p className="mb-0 color-gray body-text">
                    
                    {t('about.mission.description')}
                  </p>
                </div>
              </div>
            </Col>
            <Col md={12} className="px-0 vision-column">
              <div id="vision" className="d-flex flex-md-row flex-column align-items-stretch mission-vision-section p-3" style={{ background: GOLD, overflow: 'hidden' }}>
                <div className="px-4 py-1 d-flex flex-column justify-content-start" style={{ flex: 1 }}>
                  <div className="d-flex align-items-center mb-2">
                    <span className="vision-header-bar me-3"></span>
                    <h5 className="fw-bold text-uppercase text-white mb-2 header-2">{t('about.vision.title')}</h5>
                  </div>
                  <p className="mb-0 color-gray body-text">
                    <span className='body-text color-gray'>{t('about.history.company')}</span>
                    <span className='body-text-header color-gray'>{t('about.vision.header')}</span> 
                    {t('about.vision.description')}
                  </p>
                </div>
                <img src="/images/About_us/8.jpg" alt="Our Vision" className="image-hover-animation" />
              </div>
            </Col>
          </Row>

          {/* Core Value */}
          <Row>
            <Col md={12} className="text-center mb-3">
              <h4 className="fw-bold text-uppercase color-brown header-2 mb-0 about-core-value">{t('about.coreValues.title')}</h4>
              <div className="d-flex justify-content-center align-items-center">
                <div className="fw-bold text-uppercase mb-4 body-text color-light text-center" style={{ background: GOLD, width: 200 }}>
                  {t('about.coreValues.focusOn')}
                </div>
              </div>
            </Col>
            <Col md={12} className="px-0">
              <div className="d-flex flex-row flex-wrap justify-content-center align-items-stretch gap-1 w-100">
                {coreValues.map((val, idx) => (
                  <div key={val.letter} className='core-value-img-container' style={{ position: 'relative', width: 325, height: 324, borderRadius: 0, overflow: 'hidden', background: GOLD }}>
                    <img src={val.img} alt={val.label} className="image-hover-animation" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.85 }} />
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(to top,rgba(227, 195, 81, 0.76) 10%, transparent 100%)', zIndex: 1 }} />
                    <div style={{ 
                      position: 'absolute', 
                      top: '0%', 
                      left: 0, 
                      width: '100%', 
                      textAlign: 'center', 
                      fontSize: 180, 
                      fontWeight: 700, 
                      color: '#fff', 
                      zIndex: 2, 
                      letterSpacing: 8, 
                      textShadow: '0 2px 8px rgba(0,0,0,0.18)',
                      fontFamily: i18n.language === 'km' ? "'Saira Condensed', sans-serif" : 'inherit'
                    }}>{val.letter}</div>
                    <div style={{ position: 'absolute', bottom: 16, left: 0, width: '100%', textAlign: 'center', fontWeight: 700, color: '#fff', zIndex: 2, fontSize: 20, letterSpacing: 1 }}>{t(`about.coreValues.${val.label.toLowerCase()}`)}</div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default About; 